import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowRight, ChevronRight, Gift, PartyPopper, Trophy } from 'lucide-react'
import { useAuth } from '@/providers/auth'
import { usePermission } from '@/providers/permissions'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { useCompanyConfig } from '@/hooks/useCompanyConfig'
import { useMissions, useMissionsHidden } from '@/hooks/useMissions'
import {
  MISSIONS_PATH,
  MISSION_COPY,
  SOCIAL_PROFILES,
  levelProgress,
  missionPath,
  missionsComplete,
  missionsDone,
  missionsUnlocked,
  nextPendingMission
} from '@/lib/missions'
import { cn } from '@/lib/utils'

const SPOTLIGHT_MS = 4000

interface NextStepsCardProps {
  collapsed?: boolean
  onNavigate?: () => void
}

export function NextStepsCard(props: NextStepsCardProps) {
  const { hasPermission } = usePermission()
  const { data } = useCompanyConfig()
  const { hidden, hide } = useMissionsHidden()

  if (!hasPermission('settings', 'write') || !missionsUnlocked(data?.onboarding) || hidden) return null
  return <NextStepsPanel {...props} onHide={hide} />
}

function NextStepsPanel({ collapsed, onNavigate, onHide }: NextStepsCardProps & { onHide: () => void }) {
  const { company } = useAuth()
  const { board, claim } = useMissions()
  const location = useLocation()
  const [spotlight, setSpotlight] = useState(() => (location.state as { nextSteps?: string } | null)?.nextSteps === 'later')
  const completeOnArrival = useRef<boolean | null>(null)

  useEffect(() => {
    if (!spotlight) return
    const timer = setTimeout(() => setSpotlight(false), SPOTLIGHT_MS)
    return () => clearTimeout(timer)
  }, [spotlight])

  if (!board) return null
  const complete = missionsComplete(board)
  completeOnArrival.current ??= complete
  if (complete && completeOnArrival.current) return null

  const done = missionsDone(board)
  const label = `${done} de ${board.missions.length} missões`
  const next = nextPendingMission(board)
  const ring = spotlight && 'ring-2 ring-primary ring-offset-2 ring-offset-sidebar'

  if (collapsed) {
    return (
      <div className="flex justify-center p-3" data-tour="next-steps">
        <Button variant="outline" size="icon" asChild className={cn(ring)}>
          <Link to={MISSIONS_PATH} onClick={onNavigate} aria-label={`Próximos passos: ${label}, nível ${board.level.level}`}>
            <Trophy />
          </Link>
        </Button>
      </div>
    )
  }

  const nextPath = next ? missionPath(next.id, company.id) : null
  const profile = next ? SOCIAL_PROFILES[next.id] : undefined

  return (
    <div className="p-3 pb-1">
      <div
        className={cn(
          'rounded-xl border border-sidebar-border bg-card p-3 transition-shadow duration-500 animate-in fade-in-0 slide-in-from-bottom-3 motion-reduce:animate-none',
          ring
        )}
        data-testid="next-steps-card"
        data-tour="next-steps"
      >
        {complete ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-success/15 text-success">
                <PartyPopper className="h-4 w-4" />
              </span>
              <div className="min-w-0 leading-tight">
                <p className="text-sm font-semibold">Missões completas!</p>
                <p className="tnum text-xs text-muted-foreground">
                  Nível {board.level.level} · {board.days.earned} dias ganhos
                </p>
              </div>
            </div>
            <Button size="sm" className="w-full" onClick={onHide}>
              Concluir
            </Button>
          </div>
        ) : (
          <>
            <Button
              variant="ghost"
              size="bare"
              asChild
              className="-m-1 w-[calc(100%+0.5rem)] justify-start gap-2 rounded-lg p-1 text-left"
              data-testid="next-steps-open"
            >
              <Link to={MISSIONS_PATH} onClick={onNavigate} aria-label={`Ver todas as missões: ${label}`}>
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-highlight text-highlight-foreground">
                  <Trophy />
                </span>
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block truncate font-semibold">Próximos passos</span>
                  <span className="tnum block truncate text-xs font-normal text-muted-foreground">
                    Nível {board.level.level} · {done} de {board.missions.length}
                  </span>
                </span>
                <ChevronRight className="text-muted-foreground" />
              </Link>
            </Button>
            <Progress
              value={levelProgress(board).percent}
              size="sm"
              className="mt-3"
              aria-label={`${board.points} pontos, nível ${board.level.level}`}
            />
            <p className="tnum mt-2 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="next-steps-days">
              <Gift className="h-3.5 w-3.5 shrink-0 text-primary" />
              Você já ganhou {board.days.earned} de {board.days.cap} dias
            </p>
            {next && (nextPath || profile) && (
              <Button variant="outline" size="sm" asChild className="mt-3 w-full justify-between">
                {profile ? (
                  <a href={profile} target="_blank" rel="noopener noreferrer" onClick={() => claim(next.id)}>
                    <span className="truncate">{MISSION_COPY[next.id].title}</span>
                    <ArrowRight />
                  </a>
                ) : (
                  <Link to={nextPath!} onClick={onNavigate}>
                    <span className="truncate">{MISSION_COPY[next.id].action}</span>
                    <ArrowRight />
                  </Link>
                )}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
